/**
 * AgentHandoffCard
 *
 * The in-thread record of a conversation crossing from one agent to another,
 * and the teleport between the two threads. One handoff produces two of these:
 *
 *   • OUTBOUND — sits in the origin thread. Reads "<from> → <to>" and travels
 *     FORWARD into the branch.
 *   • INBOUND  — sits at the top of the branch. Same two agents in the same
 *     order (so the direction of the work never visually flips) but it travels
 *     BACK to the origin.
 *
 * Both sides render identically apart from their caption and arrow direction,
 * which is what makes the lineage feel like one object seen from either end.
 */

import { BotAvatar } from '@/components/common/BotAvatar';
import { useLanguage } from '@/hooks/useLanguage';
import type { AgentHandoff, AgentHandoffParty } from '@/types/models';
import './AgentHandoffCard.css';

interface AgentHandoffCardProps {
  handoff: AgentHandoff;
  /** Navigate to the linked conversation, scrolling to the twin marker. */
  onTeleport: (conversationId: string, messageId?: string) => void;
  /** Open the linked conversation BESIDE this one instead of navigating. */
  onOpenSplit?: (conversationId: string) => void;
}

function AgentTile({ party, label }: { party: AgentHandoffParty; label: string }) {
  return (
    <span className="handoff-agent" title={party.name}>
      <BotAvatar
        name={party.name || 'AI'}
        size={30}
        colorOverride={party.avatar_color as any}
        variantOverride={party.avatar_variant ?? undefined}
      />
      <span className="handoff-agent-text">
        <span className="handoff-agent-role">{label}</span>
        <span className="handoff-agent-name" dir="auto">{party.name}</span>
      </span>
    </span>
  );
}

export function AgentHandoffCard({ handoff, onTeleport, onOpenSplit }: AgentHandoffCardProps) {
  const { t } = useLanguage();
  const isOutbound = handoff.direction === 'outbound';

  const caption = isOutbound
    ? (t('chat.agentBranch.outboundCaption') || 'Conversation continued with another agent')
    : (t('chat.agentBranch.inboundCaption') || 'Conversation carried over from another agent');

  const cta = isOutbound
    ? (t('chat.agentBranch.openBranch') || 'Open this conversation')
    : (t('chat.agentBranch.backToOrigin') || 'Back to the original conversation');

  return (
    <div className={`handoff-card-wrap ${isOutbound ? 'is-outbound' : 'is-inbound'}`}>
      {/* The whole card is a click target for the DEFAULT action (jump), with
          the two buttons inside as the accessible, keyboard-reachable controls.
          It can't be a <button> itself — that would nest controls — so this is
          a redundant convenience target, not the accessible path. */}
      <div
        className="handoff-card is-clickable"
        role="group"
        aria-label={caption}
        onClick={() => onTeleport(handoff.target_conversation_id, handoff.target_message_id)}
      >
        <span className="handoff-card-caption">{caption}</span>

        <span className="handoff-card-route">
          <AgentTile
            party={handoff.from}
            label={t('chat.agentBranch.fromLabel') || 'From'}
          />

          {/* Arrow is decorative: the caption above already states the
              direction for assistive tech, so this stays aria-hidden. In RTL
              the CSS mirrors it, which is why it's an inline SVG and not a
              literal "→" character. */}
          <span className="handoff-card-arrow" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="4" y1="12" x2="19" y2="12" />
              <polyline points="13 6 19 12 13 18" />
            </svg>
          </span>

          <AgentTile
            party={handoff.to}
            label={t('chat.agentBranch.toLabel') || 'To'}
          />
        </span>

        <div className="handoff-card-actions">
          <button
            type="button"
            className="handoff-card-cta"
            onClick={(e) => {
              // The card behind this button does the same thing; stop here so
              // one click isn't counted twice.
              e.stopPropagation();
              onTeleport(handoff.target_conversation_id, handoff.target_message_id);
            }}
            aria-label={`${caption} — ${cta}`}
          >
            <span dir="auto" className="handoff-card-cta-text">{cta}</span>
            <span className="handoff-card-cta-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 6 15 12 9 18" />
              </svg>
            </span>
          </button>
          {onOpenSplit && (
            <button
              type="button"
              className="handoff-card-split"
              onClick={(e) => {
                // Must not fall through to the card's default jump.
                e.stopPropagation();
                onOpenSplit(handoff.target_conversation_id);
              }}
              title={t('chat.openInSplitScreen') || 'Open in split screen'}
              aria-label={t('chat.openInSplitScreen') || 'Open in split screen'}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <rect x="2" y="3" width="20" height="18" rx="2" />
                <line x1="12" y1="3" x2="12" y2="21" />
              </svg>
              <span>{t('chat.openInSplitScreen') || 'Open in split screen'}</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default AgentHandoffCard;
